import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface ApartmentUnitRow {
  id: string;
  tower: string;
  apartment: string;
  coefficient: string;
}

export function createEmptyApartmentUnit(): ApartmentUnitRow {
  return {
    id: `unit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    tower: '',
    apartment: '',
    coefficient: '',
  };
}

export function parseApartmentUnitsFromProfile(
  apartmentCsv?: string,
  totalCoefficient?: number | string | null
): ApartmentUnitRow[] {
  const apartments = (apartmentCsv || '')
    .split(',')
    .map((apt) => apt.trim())
    .filter(Boolean);

  if (apartments.length === 0) {
    return [createEmptyApartmentUnit()];
  }

  const total =
    totalCoefficient != null && totalCoefficient !== ''
      ? Number(totalCoefficient)
      : 0;
  const safeTotal = Number.isFinite(total) ? total : 0;
  const perUnit = apartments.length > 0 ? safeTotal / apartments.length : 0;

  return apartments.map((apartment, index) => {
    const coefficient =
      index === apartments.length - 1
        ? Number((safeTotal - perUnit * (apartments.length - 1)).toFixed(11))
        : Number(perUnit.toFixed(11));

    const dashIndex = apartment.indexOf('-');
    const tower = dashIndex > 0 ? apartment.slice(0, dashIndex) : '';
    const aptNumber = dashIndex > 0 ? apartment.slice(dashIndex + 1) : apartment;

    return {
      id: `unit-${index}-${apartment}`,
      tower,
      apartment: aptNumber,
      coefficient: Number.isFinite(coefficient) ? String(coefficient) : '',
    };
  });
}

export function composeApartmentLabel(tower: string, apartment: string): string {
  const apt = apartment.trim();
  const tw = tower.trim();
  if (!apt) return '';
  if (tw && !apt.includes('-')) return `${tw}-${apt}`;
  return apt;
}

export function serializeApartmentUnits(units: ApartmentUnitRow[]): Array<{
  apartment: string;
  coefficient: number;
}> {
  return units
    .map((unit) => ({
      apartment: composeApartmentLabel(unit.tower, unit.apartment),
      coefficient: Number(unit.coefficient),
    }))
    .filter((unit) => unit.apartment);
}

export function validateApartmentUnits(
  units: ApartmentUnitRow[],
  options?: { required?: boolean }
): string | null {
  const required = options?.required !== false;
  const filledUnits = units.filter((unit) => composeApartmentLabel(unit.tower, unit.apartment));

  if (filledUnits.length === 0) {
    return required ? 'Agrega al menos una unidad' : null;
  }

  const seen = new Set<string>();
  for (let i = 0; i < filledUnits.length; i++) {
    const unit = filledUnits[i];
    const label = composeApartmentLabel(unit.tower, unit.apartment);
    const coef = Number(unit.coefficient);
    if (unit.coefficient.trim() === '' || !Number.isFinite(coef) || coef < 0) {
      return `Unidad ${i + 1}: coeficiente inválido (≥ 0)`;
    }
    const key = label.toLowerCase();
    if (seen.has(key)) {
      return `Apartamento duplicado: ${label}`;
    }
    seen.add(key);
  }

  return null;
}

interface ApartmentUnitsEditorProps {
  units: ApartmentUnitRow[];
  onChange: (units: ApartmentUnitRow[]) => void;
  showTower?: boolean;
  className?: string;
}

export const ApartmentUnitsEditor: React.FC<ApartmentUnitsEditorProps> = ({
  units,
  onChange,
  showTower = true,
  className,
}) => {
  const totalCoefficient = useMemo(() => {
    return units.reduce((sum, unit) => {
      const value = Number(unit.coefficient);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }, [units]);

  const filledCount = useMemo(() => {
    return units.filter((unit) => composeApartmentLabel(unit.tower, unit.apartment)).length;
  }, [units]);

  const updateUnit = (id: string, patch: Partial<ApartmentUnitRow>) => {
    onChange(units.map((unit) => (unit.id === id ? { ...unit, ...patch } : unit)));
  };

  const removeUnit = (id: string) => {
    if (units.length <= 1) {
      onChange([createEmptyApartmentUnit()]);
      return;
    }
    onChange(units.filter((unit) => unit.id !== id));
  };

  const addUnit = () => {
    onChange([...units, createEmptyApartmentUnit()]);
  };

  return (
    <div className={cn('rounded-lg border border-slate-200 bg-slate-50/60', className)}>
      <div className="border-b border-slate-200 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Unidades</p>
            <p className="text-xs text-slate-500">Torre, apartamento y coeficiente por unidad</p>
          </div>
          <button
            type="button"
            onClick={addUnit}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            <Plus className="h-3.5 w-3.5" />
            Agregar unidad
          </button>
        </div>
      </div>

      <div className="space-y-2 px-3 py-3">
        <div
          className={cn(
            'grid gap-2 px-1 text-[11px] font-medium uppercase tracking-wide text-slate-500',
            showTower ? 'grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)_minmax(0,1fr)_2rem]' : 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem]'
          )}
        >
          {showTower ? <span>Torre</span> : null}
          <span>Apartamento</span>
          <span>Coeficiente</span>
          <span className="sr-only">Quitar</span>
        </div>

        {units.map((unit, index) => (
          <div
            key={unit.id}
            className={cn(
              'grid items-center gap-2',
              showTower ? 'grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)_minmax(0,1fr)_2rem]' : 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem]'
            )}
          >
            {showTower ? (
              <input
                type="text"
                value={unit.tower}
                onChange={(e) => updateUnit(unit.id, { tower: e.target.value })}
                placeholder="4"
                aria-label={`Torre unidad ${index + 1}`}
                className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : null}
            <input
              type="text"
              value={unit.apartment}
              onChange={(e) => updateUnit(unit.id, { apartment: e.target.value })}
              placeholder="101"
              aria-label={`Apartamento unidad ${index + 1}`}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
            <input
              type="number"
              step="any"
              min="0"
              max="100"
              value={unit.coefficient}
              onChange={(e) => updateUnit(unit.id, { coefficient: e.target.value })}
              placeholder="0"
              aria-label={`Coeficiente unidad ${index + 1}`}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
            <button
              type="button"
              onClick={() => removeUnit(unit.id)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600"
              aria-label={`Quitar unidad ${index + 1}`}
              title="Quitar unidad"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-slate-200 bg-white/95 px-3 py-2.5 backdrop-blur-sm">
        <p className="text-xs text-slate-500">
          {filledCount} {filledCount === 1 ? 'apartamento' : 'apartamentos'}
        </p>
        <p className="text-sm font-semibold text-slate-800">
          Total coeficiente:{' '}
          <span className="tabular-nums text-blue-700">{totalCoefficient.toFixed(4)}</span>
        </p>
      </div>
    </div>
  );
};
